import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from 'lodash';
import moment from "moment-timezone";
import { DATE_FORMAT } from "../app/contants";
import * as api from './formApi';

const TIMEZONE_EST = 'America/New_York';

/**
 * Get form json schema
 */
export const getSchema = createAsyncThunk(
	'form/getSchema',
	async (token, { dispatch }) => {
		const response = await api.getSchema(token);

		if (response.status === 500) {
			throw new Error(response.statusText)
		}

		return response.json();
	}
)

/**
 * Save form data
 */
export const saveData = createAsyncThunk(
	'form/saveData',
	async (payload, { getState, rejectWithValue }) => {
		try {
			const response = await api.saveData(_.pick(payload, ['action', 'token', 'data']));
			let responseBody = await response.json();
			if (response.status === 401) {
				throw new Error("Unauthorized");
			} else if (response.status === 500) {
				throw new Error(response.statusText)
			}
			return responseBody;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
)

/**
 * Get user profile information
 */
export const getUserProfile = createAsyncThunk(
	"form/userProfile",
	async (_, { getState, rejectWithValue }) => {
		try {
			const response = await api.getUserProfile();
			if (response.status === 401) {
				throw new Error("Unauthorized");
			}
			return response.json();
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

/**
 * Get client clinician and supervisors
 */
export const getClientSupervisors = createAsyncThunk(
	'form/getClientSupervisors',
	async (clientId) => {
		const response = await api.getClientSupervisors(clientId);

		if (response.status === 500) {
			throw new Error(response.statusText)
		}

		return response.json();
	}
)

/**
 * Create a note
 */
export const createNote = createAsyncThunk(
	'form/createNote',
	async (data) => {
		const response = await api.createNote(data);
		return response.json();
	}
)

const formSlice = createSlice({
	name: "form",
	initialState: {
		clientId: null,
		formName: "",
		formData: {},
		isLinkExpired: false,
		isLoading: true,
		isProfileEmpty: false,
		liveValidation: false,
		role: "client",
		schema: {},
		showAlert: false,
		status: null,
		uiSchema: {},
		userAction: 'save',
		validationEnabled: true,
		clinicians: []
	},
	reducers: {
		updateFormData(state, action) {
			_.assign(state, {
				formData: action.payload
			})
		},
		enableLiveValidation(state) {
			_.assign(state, {
				liveValidation: true
			})
		},
		updateUserAction(state, action) {
			_.assign(state, {
				userAction: action.payload,
				validationEnabled: !(state.role === 'clinician' && (action.payload === 'save' || action.payload === 'save_and_send'))
			})
		},
		toggleAlert(state) {
			_.assign(state, {
				showAlert: !state.showAlert
			})
		}
	},
	extraReducers: {
		[getSchema.fulfilled]: (state, action) => {
			_.assign(state, {
				clientId: action.payload.clientId,
				formData: action.payload.formData,
				formName: action.payload.formName,
				isLoading: false,
				role: action.payload.role,
				status: null,
				schema: action.payload.schema,
				uiSchema: action.payload.uiSchema
			})
		},
		[getSchema.pending]: (state) => {
			_.assign(state, {
				isLoading: true
			})
		},
		[getSchema.rejected]: (state) => {
			_.assign(state, {
				isLoading: false,
				isLinkExpired: true,
				showAlert: true
			})
		},
		[saveData.fulfilled]: (state, action) => {
			_.assign(state, {
				isLoading: false,
				status: action.payload.status,
				showAlert: true
			})
		},
		[saveData.pending]: (state) => {
			_.assign(state, {
				isLoading: true
			})
		},
		[saveData.rejected]: (state, action) => {
			if (action.payload === "Unauthorized") {
				_.assign(state, {
					isLoading: false,
					showAlert: true,
					status: "UNAUTHORIZED",
				});
			}
		},
		[getUserProfile.pending]: (state, action) => {
			_.assign(state, {
				isLoading: true
			})
		},
		[getUserProfile.fulfilled]: (state, action) => {
			_.assign(state, {
				isLoading: false,
				enableLiveValidation: true
			})

			let payload = action.payload;

			if (payload.status !== "NOT_FOUND") {
				let name = `${payload.firstName} ${payload.lastName}, ${payload.degreeAndLicense}`;

				if (action.meta.arg.role === 'clinician') {
					_.assign(state.formData, {
						clinicianInfo: {
							name,
							signature: payload.signature,
							date: moment().tz(TIMEZONE_EST).format(DATE_FORMAT)
						}
					})
				} else if (action.meta.arg.role === 'supervisor') {
					_.assign(state.formData, {
						supervisorInfo: {
							name,
							signature: payload.signature,
							date: moment().tz(TIMEZONE_EST).format(DATE_FORMAT)
						}
					})
				}
			} else {
				_.assign(state, {
					isProfileEmpty: true,
					showAlert: true
				})
			}
		},
		[getUserProfile.rejected]: (state, action) => {
			if (action.payload === "Unauthorized") {
				_.assign(state, {
					isLoading: false,
					showAlert: true,
					status: "UNAUTHORIZED",
				});
			}
		},
		[getClientSupervisors.pending]: (state, action) => {
			_.assign(state, {
				isLoading: true
			})
		},
		[getClientSupervisors.fulfilled]: (state, action) => {
			_.assign(state, {
				clinicians: action.payload,
				isLoading: false
			})
		},
	}
})

export const { updateFormData, enableLiveValidation, updateUserAction, toggleAlert } = formSlice.actions;

export default formSlice.reducer;